
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

export default function SupportPage() {
  const { getTotalItems } = useCart();
  const [searchQuery, setSearchQuery] = useState('');

  const supportCategories = [
    {
      title: 'Account & Orders',
      icon: 'ri-user-line',
      description: 'Manage your account, view orders, and update information',
      topics: ['Order Status', 'Account Settings', 'Payment Issues', 'Order History']
    },
    {
      title: 'Digital Products',
      icon: 'ri-download-line',
      description: 'Help with game keys, downloads, and activation',
      topics: ['Key Activation', 'Download Issues', 'Platform Support', 'Product Keys']
    },
    {
      title: 'Technical Support',
      icon: 'ri-tools-line',
      description: 'Technical issues and troubleshooting',
      topics: ['Website Issues', 'Login Problems', 'Browser Support', 'Mobile App']
    },
    {
      title: 'Billing & Refunds',
      icon: 'ri-money-dollar-circle-line',
      description: 'Payment, billing, and refund assistance',
      topics: ['Refund Requests', 'Payment Methods', 'Billing Questions', 'Pricing']
    }
  ];

  const quickActions = [
    {
      title: 'Live Chat',
      description: 'Chat with our support team',
      icon: 'ri-chat-3-line',
      status: 'Online',
      action: 'Start Chat'
    },
    {
      title: 'Email Support',
      description: 'Send us a detailed message',
      icon: 'ri-mail-line',
      status: 'Available',
      action: 'Send Email'
    },
    {
      title: 'Phone Support',
      description: 'Call our support hotline',
      icon: 'ri-phone-line',
      status: 'Available',
      action: 'Call Now'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <Header cartItemsCount={getTotalItems()} />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Support Center</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Get help with your orders, account, and technical issues. Our support team is here 24/7.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for help articles, FAQs, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-800 text-white px-6 py-4 pl-14 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none text-lg"
            />
            <i className="ri-search-line absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
          </div>
        </div>

        {/* Quick Actions */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickActions.map((action, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <i className={`${action.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{action.title}</h3>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-green-400 text-sm">{action.status}</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-4">{action.description}</p>
                {action.action === 'Send Email' ? (
                  <Link to="/contact">
                    <button className="bg-yellow-400 hover:bg-yellow-500 text-black px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap w-full">
                      {action.action}
                    </button>
                  </Link>
                ) : (
                  <button className="bg-yellow-400 hover:bg-yellow-500 text-black px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap w-full">
                    {action.action}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Support Categories */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Support Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {supportCategories.map((category, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-purple-500 transition-colors cursor-pointer">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                    <i className={`${category.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">{category.title}</h3>
                  </div>
                </div>
                <p className="text-gray-300 text-sm mb-4">{category.description}</p>
                <div className="flex flex-wrap gap-2">
                  {category.topics.map((topic, topicIndex) => (
                    <span key={topicIndex} className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-xs">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* System Status */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">System Status</h2>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <span className="text-green-400 font-semibold">All Systems Operational</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Website</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-green-400 text-sm">Operational</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Payment System</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-green-400 text-sm">Operational</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-300">Key Delivery</span>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-green-400 text-sm">Operational</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Popular Articles */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-6">Popular Help Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'How to activate your game key',
              'Refund policy and process',
              'Supported payment methods',
              'Account security tips',
              'Download and installation guide',
              'Troubleshooting common issues'
            ].map((article, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-purple-500 transition-colors cursor-pointer">
                <div className="flex items-center gap-3">
                  <i className="ri-article-line text-purple-400"></i>
                  <span className="text-white text-sm">{article}</span>
                  <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
}
